// app/career-map/index.tsx
// Career Paths — Full Production Listing Page
// Features: search, category filter, demand filter, bookmarks (AsyncStorage), compare mode, compare modal

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import Head from 'expo-router/head';
import { useRouter } from 'expo-router';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  ArrowRight,
  BookmarkCheck,
  BookmarkPlus,
  ChevronDown,
  GitCompare,
  Search,
  Sparkles,
  TrendingUp,
  X,
} from 'lucide-react-native';
import { theme } from '@/constants/theme';
import { trackPageView, trackButtonClick, trackFunnelStep } from '@/utils/analytics';
import {
  CAREERS,
  CATEGORY_COLORS,
  CATEGORY_EMOJIS,
  CATEGORY_LABELS,
  DEMAND_COLORS,
  DEMAND_LABELS,
  Career,
  CategoryType,
  DemandType,
  filterCareers,
  formatSalary,
} from '@/utils/careerData';

// ─── Constants ──────────────────────────────────────────────────────────────

const BG           = '#F2EBDD';
const TEXT         = '#080C18';
const MUTED        = '#7A6F5F';
const CARD         = '#FAF5EA';
const BORDER       = 'rgba(15,23,42,0.08)';
const PRIMARY      = '#C8531E';
const PRIMARY_SOFT = 'rgba(200,83,30,0.18)';
const MAX_COMPARE  = 3;
const SAVED_KEY    = 'career_saved_v1';

const CATEGORIES: Array<{ key: CategoryType | null; label: string }> = [
  { key: null,        label: 'הכל'    },
  { key: 'software',  label: 'פיתוח'  },
  { key: 'data',      label: 'דאטה'   },
  { key: 'cyber',     label: 'סייבר'  },
  { key: 'design',    label: 'עיצוב'  },
  { key: 'marketing', label: 'שיווק'  },
  { key: 'business',  label: 'עסקים'  },
];

const DEMANDS: Array<{ key: DemandType | null; label: string }> = [
  { key: null,      label: 'כל הביקוש'  },
  { key: 'hot',     label: '🔥 חם מאוד' },
  { key: 'growing', label: '📈 גדל'      },
  { key: 'stable',  label: '✅ יציב'      },
];

// ─── Sub-components ─────────────────────────────────────────────────────────

function DemandBadge({ demand }: { demand: DemandType }) {
  const c = DEMAND_COLORS[demand];
  return (
    <View style={[ss.badge, { backgroundColor: c.bg, borderColor: c.border }]}>
      <Text style={[ss.badgeText, { color: c.text }]}>{DEMAND_LABELS[demand]}</Text>
    </View>
  );
}

function CategoryDot({ category }: { category: CategoryType }) {
  return (
    <View style={[ss.catDot, { backgroundColor: CATEGORY_COLORS[category] }]} />
  );
}

interface CareerCardProps {
  career: Career;
  saved: boolean;
  compareSelected: boolean;
  compareDisabled: boolean;
  onPress: () => void;
  onSave: () => void;
  onCompareToggle: () => void;
}

function CareerCard({
  career,
  saved,
  compareSelected,
  compareDisabled,
  onPress,
  onSave,
  onCompareToggle,
}: CareerCardProps) {
  const catColor  = CATEGORY_COLORS[career.category];
  const topSkills = career.skills.filter((s) => s.required).slice(0, 3);

  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [ss.card, pressed && ss.cardPressed]}
      accessibilityRole="button"
      accessibilityLabel={`פתח מסלול ${career.title}`}
    >
      {/* Header strip */}
      <View style={[ss.cardStrip, { backgroundColor: catColor + '18' }]}>
        <View style={ss.cardStripRow}>
          <Text style={ss.cardEmoji}>{career.emoji}</Text>
          <View style={ss.cardStripRight}>
            <View style={ss.catRow}>
              <CategoryDot category={career.category} />
              <Text style={[ss.catLabel, { color: catColor }]}>
                {CATEGORY_LABELS[career.category]}
              </Text>
            </View>
            <DemandBadge demand={career.demand} />
          </View>
        </View>
      </View>

      {/* Body */}
      <View style={ss.cardBody}>
        <Text style={ss.cardTitle}>{career.title}</Text>
        <Text style={ss.cardDesc} numberOfLines={2}>{career.description}</Text>

        {/* Salary + Time */}
        <View style={ss.metaRow}>
          <View style={ss.metaChip}>
            <Text style={ss.metaLabel}>משכורת</Text>
            <Text style={ss.metaValue}>
              {formatSalary(career.salaryMin)}–{formatSalary(career.salaryMax)}
            </Text>
          </View>
          <View style={ss.metaDivider} />
          <View style={ss.metaChip}>
            <Text style={ss.metaLabel}>זמן לימוד</Text>
            <Text style={ss.metaValue}>{career.timeToLearn}</Text>
          </View>
          <View style={ss.metaDivider} />
          <View style={ss.metaChip}>
            <Text style={ss.metaLabel}>צמיחה/שנה</Text>
            <Text style={[ss.metaValue, { color: PRIMARY }]}>+{career.jobGrowthRate}%</Text>
          </View>
        </View>

        {/* Top skills */}
        {topSkills.length > 0 && (
          <View style={ss.skillsRow}>
            {topSkills.map((sk) => (
              <View key={sk.name} style={ss.skillChip}>
                <Text style={ss.skillChipText}>{sk.name}</Text>
              </View>
            ))}
          </View>
        )}

        {/* Actions */}
        <View style={ss.cardActions}>
          <Pressable
            onPress={onPress}
            style={({ pressed }) => [ss.primaryBtn, pressed && { opacity: 0.88 }]}
            accessibilityRole="button"
            accessibilityLabel={`ראה מסלול ${career.title}`}
          >
            <ArrowRight size={14} color="#FAF5EA" />
            <Text style={ss.primaryBtnText}>ראה מסלול מלא</Text>
          </Pressable>

          <View style={ss.iconBtns}>
            {/* Save */}
            <Pressable
              onPress={(e) => { e.stopPropagation?.(); onSave(); }}
              style={({ pressed }) => [ss.iconBtn, saved && ss.iconBtnActive, pressed && { opacity: 0.8 }]}
              accessibilityRole="button"
              accessibilityLabel={saved ? 'הסר מהשמורים' : 'שמור מסלול'}
            >
              {saved
                ? <BookmarkCheck size={16} color={PRIMARY} />
                : <BookmarkPlus  size={16} color={MUTED}   />
              }
            </Pressable>

            {/* Compare */}
            <Pressable
              onPress={(e) => { e.stopPropagation?.(); onCompareToggle(); }}
              disabled={compareDisabled && !compareSelected}
              style={({ pressed }) => [
                ss.iconBtn,
                compareSelected && ss.compareActive,
                compareDisabled && !compareSelected && ss.iconBtnDisabled,
                pressed && { opacity: 0.8 },
              ]}
              accessibilityRole="button"
              accessibilityLabel={compareSelected ? 'הסר מהשוואה' : 'הוסף להשוואה'}
            >
              <GitCompare size={16} color={compareSelected ? PRIMARY : compareDisabled ? '#CDC3AE' : MUTED} />
            </Pressable>
          </View>
        </View>
      </View>
    </Pressable>
  );
}

// Compare Row in modal
function CompareRow({ label, values }: { label: string; values: string[] }) {
  return (
    <View style={ss.cmpRow}>
      <Text style={ss.cmpLabel}>{label}</Text>
      {values.map((v, i) => (
        <Text key={i} style={ss.cmpValue}>{v}</Text>
      ))}
    </View>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function CareerMapIndex() {
  const router = useRouter();

  const [q,            setQ]            = useState('');
  const [category,     setCategory]     = useState<CategoryType | null>(null);
  const [demand,       setDemand]        = useState<DemandType   | null>(null);
  const [savedSlugs,   setSavedSlugs]   = useState<string[]>([]);
  const [compareSlugs, setCompareSlugs] = useState<string[]>([]);
  const [showCompare,  setShowCompare]  = useState(false);
  const [showSaved,    setShowSaved]    = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const [showDemandDD, setShowDemandDD] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ── Load saved from AsyncStorage ──
  useEffect(() => {
    (async () => {
      try {
        const raw = await AsyncStorage.getItem(SAVED_KEY);
        if (mountedRef.current) {
          setSavedSlugs(raw ? JSON.parse(raw) : []);
        }
      } catch {
        // quiet
      } finally {
        if (mountedRef.current) setLoadingSaved(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    const id = 'career-map-breadcrumb-jsonld';
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = id;
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'דף הבית', item: 'https://kursiaii.com' },
        { '@type': 'ListItem', position: 2, name: 'מסלולי קריירה', item: 'https://kursiaii.com/career-map' },
      ],
    });
    document.head.appendChild(script);
    return () => { const el = document.getElementById(id); if (el) el.remove(); };
  }, []);

  // ── Analytics ──
  useEffect(() => {
    void trackPageView('/career-map');
    void trackFunnelStep('landing', { page: 'career-map' });
  }, []);

  // ── Filtered list ──
  const filtered = useMemo(() => filterCareers(q, category, demand), [q, category, demand]);
  const savedCareers   = useMemo(() => CAREERS.filter((c) => savedSlugs.includes(c.slug)),   [savedSlugs]);
  const compareCareers = useMemo(() => CAREERS.filter((c) => compareSlugs.includes(c.slug)), [compareSlugs]);

  // ── Save toggle ──
  const toggleSave = useCallback(async (slug: string) => {
    setSavedSlugs((prev) => {
      const next = prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug];
      void AsyncStorage.setItem(SAVED_KEY, JSON.stringify(next));
      return next;
    });
    void trackButtonClick('career_save', '/career-map', { slug });
  }, []);

  // ── Compare toggle ──
  const toggleCompare = useCallback((slug: string) => {
    setCompareSlugs((prev) => {
      if (prev.includes(slug)) return prev.filter((s) => s !== slug);
      if (prev.length >= MAX_COMPARE) return prev;
      return [...prev, slug];
    });
  }, []);

  // ── Navigate to career detail ──
  const goCareer = useCallback((slug: string) => {
    void trackButtonClick('career_open', '/career-map', { slug });
    router.push({ pathname: '/career-map/[slug]', params: { slug } } as any);
  }, [router]);

  // ── Stats ──
  const hotCount    = CAREERS.filter((c) => c.demand === 'hot').length;
  const maxSalary   = Math.max(...CAREERS.map((c) => c.salaryMax));
  const totalJobs   = CAREERS.reduce((acc, c) => acc + c.avgMonthlyJobs, 0);

  return (
    <>
      {Platform.OS === 'web' && (
        <Head>
          <title>מסלולי קריירה בהייטק | Kursi — משכורות, כישורים ומפת למידה</title>
          <meta name="description" content="גלה 10+ מסלולי קריירה בהייטק — Full Stack, Data Science, DevOps, Cyber, AI ועוד. משכורות ממוצעות, כישורים נדרשים ומפת למידה מפורטת." />
          <link rel="canonical" href="https://kursiaii.com/career-map" />
          <meta name="robots" content="index, follow" />
          <meta property="og:type" content="website" />
          <meta property="og:title" content="מסלולי קריירה בהייטק | Kursi" />
          <meta property="og:description" content="גלה 10+ מסלולי קריירה בהייטק — Full Stack, Data Science, DevOps, Cyber ועוד." />
          <meta property="og:url" content="https://kursiaii.com/career-map" />
          <meta property="og:image" content="https://kursiaii.com/og-image.webp" />
        </Head>
      )}

      <ScrollView style={ss.scroll} contentContainerStyle={ss.content}>

        {/* ── Hero ── */}
        <View style={ss.hero}>
          <View style={ss.heroIconRow}>
            <View style={ss.heroIcon}>
              <TrendingUp size={20} color={PRIMARY} />
            </View>
          </View>
          <Text style={ss.heroTitle}>מסלולי קריירה בהייטק</Text>
          <Text style={ss.heroSub}>
            בחר מסלול, ראה מה צריך ללמוד, כמה מרוויחים וכמה זמן לוקח — הכל במקום אחד.
          </Text>

          {/* Stats bar */}
          <View style={ss.statsBar}>
            <View style={ss.statItem}>
              <Text style={ss.statNum}>{CAREERS.length}</Text>
              <Text style={ss.statLabel}>מסלולים</Text>
            </View>
            <View style={ss.statDivider} />
            <View style={ss.statItem}>
              <Text style={ss.statNum}>{hotCount}</Text>
              <Text style={ss.statLabel}>🔥 מבוקשים</Text>
            </View>
            <View style={ss.statDivider} />
            <View style={ss.statItem}>
              <Text style={ss.statNum}>{formatSalary(maxSalary)}+</Text>
              <Text style={ss.statLabel}>שכר מקסימום</Text>
            </View>
            <View style={ss.statDivider} />
            <View style={ss.statItem}>
              <Text style={ss.statNum}>{totalJobs.toLocaleString()}</Text>
              <Text style={ss.statLabel}>משרות/חודש</Text>
            </View>
          </View>
        </View>

        {/* ── Search ── */}
        <View style={ss.searchWrap}>
          <View style={ss.searchIcon}>
            <Search size={16} color={PRIMARY} />
          </View>
          <TextInput
            value={q}
            onChangeText={setQ}
            placeholder="חפש מסלול (לדוגמה: React, AI, סייבר, שיווק)"
            placeholderTextColor={MUTED}
            style={ss.searchInput}
            autoCorrect={false}
            autoCapitalize="none"
            returnKeyType="search"
            accessible
            accessibilityLabel="חיפוש מסלולי קריירה"
          />
          {q.length > 0 && (
            <Pressable onPress={() => setQ('')} style={ss.searchClear} accessibilityRole="button" accessibilityLabel="נקה חיפוש">
              <X size={14} color={MUTED} />
            </Pressable>
          )}
        </View>

        {/* ── Category Tabs ── */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={ss.tabsRow}
          style={ss.tabsScroll}
        >
          {CATEGORIES.map((cat) => {
            const active = category === cat.key;
            const color  = cat.key ? CATEGORY_COLORS[cat.key] : PRIMARY;
            return (
              <Pressable
                key={String(cat.key)}
                onPress={() => setCategory(active ? null : cat.key)}
                style={[ss.tab, active && { backgroundColor: color, borderColor: color }]}
                accessibilityRole="button"
                accessibilityLabel={`סנן לפי ${cat.label}`}
              >
                {cat.key && (
                  <Text style={ss.tabEmoji}>{CATEGORY_EMOJIS[cat.key]}</Text>
                )}
                <Text style={[ss.tabText, active && ss.tabTextActive]}>{cat.label}</Text>
              </Pressable>
            );
          })}
        </ScrollView>

        {/* ── Demand Filter + Actions Row ── */}
        <View style={ss.filterRow}>
          {/* Demand dropdown */}
          <View style={ss.demandWrap}>
            <Pressable
              onPress={() => setShowDemandDD((v) => !v)}
              style={[ss.demandBtn, demand && ss.demandBtnActive]}
              accessibilityRole="button"
              accessibilityLabel="סנן לפי ביקוש"
            >
              <Text style={[ss.demandBtnText, demand && { color: PRIMARY }]}>
                {demand ? DEMAND_LABELS[demand] : 'ביקוש'}
              </Text>
              <ChevronDown size={14} color={demand ? PRIMARY : MUTED} />
            </Pressable>
            {showDemandDD && (
              <View style={ss.demandDropdown}>
                {DEMANDS.map((d) => (
                  <Pressable
                    key={String(d.key)}
                    onPress={() => { setDemand(d.key); setShowDemandDD(false); }}
                    style={[ss.demandOpt, demand === d.key && ss.demandOptActive]}
                    accessibilityRole="button"
                  >
                    <Text style={[ss.demandOptText, demand === d.key && { color: PRIMARY }]}>
                      {d.label}
                    </Text>
                  </Pressable>
                ))}
              </View>
            )}
          </View>

          {/* Clear filters */}
          {(q || category || demand) && (
            <Pressable
              onPress={() => { setQ(''); setCategory(null); setDemand(null); }}
              style={ss.clearBtn}
              accessibilityRole="button"
              accessibilityLabel="נקה כל הסינונים"
            >
              <X size={12} color={MUTED} />
              <Text style={ss.clearBtnText}>נקה</Text>
            </Pressable>
          )}

          {/* Saved toggle */}
          {!loadingSaved && savedSlugs.length > 0 && (
            <Pressable
              onPress={() => setShowSaved((v) => !v)}
              style={[ss.savedToggle, showSaved && ss.savedToggleActive]}
              accessibilityRole="button"
              accessibilityLabel={showSaved ? 'הצג הכל' : 'הצג שמורים'}
            >
              <BookmarkCheck size={14} color={showSaved ? PRIMARY : MUTED} />
              <Text style={[ss.savedToggleText, showSaved && { color: PRIMARY }]}>
                שמורים ({savedSlugs.length})
              </Text>
            </Pressable>
          )}
        </View>

        {/* ── Results count ── */}
        <View style={ss.resultsMeta}>
          <Text style={ss.resultsText}>
            {showSaved
              ? `${savedCareers.length} מסלולים שמורים`
              : `${filtered.length} מסלולים`}
          </Text>
          {compareSlugs.length > 0 && (
            <Pressable
              onPress={() => setShowCompare(true)}
              style={ss.compareBar}
              accessibilityRole="button"
              accessibilityLabel="פתח השוואת מסלולים"
            >
              <GitCompare size={14} color={PRIMARY} />
              <Text style={ss.compareBarText}>
                השווה {compareSlugs.length} מסלולים
              </Text>
            </Pressable>
          )}
        </View>

        {/* ── Career Cards Grid ── */}
        <View style={ss.grid}>
          {(showSaved ? savedCareers : filtered).map((career) => (
            <CareerCard
              key={career.slug}
              career={career}
              saved={savedSlugs.includes(career.slug)}
              compareSelected={compareSlugs.includes(career.slug)}
              compareDisabled={compareSlugs.length >= MAX_COMPARE}
              onPress={() => goCareer(career.slug)}
              onSave={() => toggleSave(career.slug)}
              onCompareToggle={() => toggleCompare(career.slug)}
            />
          ))}

          {/* Empty state */}
          {(showSaved ? savedCareers : filtered).length === 0 && (
            <View style={ss.empty}>
              <Text style={ss.emptyEmoji}>🔍</Text>
              <Text style={ss.emptyTitle}>לא נמצאו מסלולים</Text>
              <Text style={ss.emptyText}>
                {showSaved
                  ? 'עדיין לא שמרת מסלולים. לחץ על 📌 בכרטיס כדי לשמור.'
                  : 'נסה מילה אחרת או שנה את הסינון.'}
              </Text>
              {showSaved && (
                <Pressable
                  onPress={() => setShowSaved(false)}
                  style={ss.emptyBtn}
                  accessibilityRole="button"
                >
                  <Text style={ss.emptyBtnText}>הצג כל המסלולים</Text>
                </Pressable>
              )}
            </View>
          )}
        </View>

        {/* ── CTA Section ── */}
        <View style={ss.ctaSection}>
          <Text style={ss.ctaTitle}>לא יודע איפה להתחיל?</Text>
          <Text style={ss.ctaSub}>ענה על 5 שאלות ונמצא לך את המסלול המושלם.</Text>
          <View style={ss.ctaBtns}>
            <Pressable
              onPress={() => { void trackButtonClick('cta_quiz', '/career-map'); router.push('/quiz' as any); }}
              style={({ pressed }) => [ss.ctaBtn, pressed && { opacity: 0.88 }]}
              accessibilityRole="button"
            >
              <Sparkles size={16} color="#FAF5EA" />
              <Text style={ss.ctaBtnText}>שאלון התאמה</Text>
            </Pressable>
            <Pressable
              onPress={() => { void trackButtonClick('cta_chat', '/career-map'); router.push('/chat' as any); }}
              style={({ pressed }) => [ss.ctaBtnSecondary, pressed && { opacity: 0.88 }]}
              accessibilityRole="button"
            >
              <Text style={ss.ctaBtnSecondaryText}>שוחח עם AI</Text>
            </Pressable>
          </View>
        </View>

      </ScrollView>

      {/* ── Sticky Compare Panel ── */}
      {compareSlugs.length >= 2 && (
        <View style={ss.comparePanel}>
          <View style={ss.comparePanelInner}>
            <Text style={ss.comparePanelText}>
              {compareSlugs.length} מסלולים נבחרו
            </Text>
            <View style={ss.comparePanelBtns}>
              <Pressable
                onPress={() => setCompareSlugs([])}
                style={ss.comparePanelClear}
                accessibilityRole="button"
                accessibilityLabel="בטל השוואה"
              >
                <X size={14} color={MUTED} />
              </Pressable>
              <Pressable
                onPress={() => { setShowCompare(true); void trackButtonClick('compare_open', '/career-map'); }}
                style={ss.comparePanelBtn}
                accessibilityRole="button"
                accessibilityLabel="פתח השוואת מסלולים"
              >
                <GitCompare size={14} color="#FAF5EA" />
                <Text style={ss.comparePanelBtnText}>השווה</Text>
              </Pressable>
            </View>
          </View>
        </View>
      )}

      {/* ── Compare Modal ── */}
      <Modal
        visible={showCompare}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowCompare(false)}
      >
        <View style={ss.modalContainer}>
          {/* Modal header */}
          <View style={ss.modalHeader}>
            <Pressable
              onPress={() => setShowCompare(false)}
              style={ss.modalClose}
              accessibilityRole="button"
              accessibilityLabel="סגור השוואה"
            >
              <X size={20} color={TEXT} />
            </Pressable>
            <Text style={ss.modalTitle}>השוואת מסלולים</Text>
          </View>

          <ScrollView style={ss.modalScroll} contentContainerStyle={ss.modalContent}>
            {/* Career headers */}
            <View style={ss.cmpHeaderRow}>
              <Text style={ss.cmpColEmpty} />
              {compareCareers.map((c) => (
                <View key={c.slug} style={ss.cmpColHeader}>
                  <Text style={ss.cmpColEmoji}>{c.emoji}</Text>
                  <Text style={ss.cmpColTitle}>{c.shortTitle}</Text>
                </View>
              ))}
            </View>

            <View style={ss.cmpDivider} />

            <CompareRow
              label="שם מסלול"
              values={compareCareers.map((c) => c.shortTitle)}
            />
            <CompareRow
              label="קטגוריה"
              values={compareCareers.map((c) => CATEGORY_LABELS[c.category])}
            />
            <CompareRow
              label="ביקוש"
              values={compareCareers.map((c) => DEMAND_LABELS[c.demand])}
            />
            <CompareRow
              label="משכורת (Junior)"
              values={compareCareers.map((c) => `${formatSalary(c.salaryMin)}–${formatSalary(c.salaryBands[0]?.max ?? c.salaryMin)}`)}
            />
            <CompareRow
              label="משכורת (Senior)"
              values={compareCareers.map((c) => `עד ${formatSalary(c.salaryMax)}`)}
            />
            <CompareRow
              label="זמן לימוד"
              values={compareCareers.map((c) => c.timeToLearn)}
            />
            <CompareRow
              label="צמיחה שנתית"
              values={compareCareers.map((c) => `+${c.jobGrowthRate}%`)}
            />
            <CompareRow
              label="משרות/חודש"
              values={compareCareers.map((c) => `~${c.avgMonthlyJobs}`)}
            />
            <CompareRow
              label="כישורים נדרשים"
              values={compareCareers.map((c) => `${c.skills.filter((s) => s.required).length} כישורים`)}
            />
            <CompareRow
              label="שלבי לימוד"
              values={compareCareers.map((c) => `${c.roadmap.length} שלבים`)}
            />

            {/* Pros comparison */}
            <Text style={ss.cmpSectionTitle}>יתרונות מרכזיים</Text>
            <View style={ss.cmpProsRow}>
              {compareCareers.map((c) => (
                <View key={c.slug} style={ss.cmpProsCol}>
                  {c.pros.slice(0, 3).map((pro, i) => (
                    <View key={i} style={ss.cmpProItem}>
                      <Text style={ss.cmpProDot}>✓</Text>
                      <Text style={ss.cmpProText}>{pro}</Text>
                    </View>
                  ))}
                </View>
              ))}
            </View>

            {/* Navigate buttons */}
            <View style={ss.cmpNavRow}>
              {compareCareers.map((c) => (
                <Pressable
                  key={c.slug}
                  onPress={() => { setShowCompare(false); goCareer(c.slug); }}
                  style={({ pressed }) => [ss.cmpNavBtn, pressed && { opacity: 0.88 }]}
                  accessibilityRole="button"
                  accessibilityLabel={`ראה מסלול ${c.title}`}
                >
                  <Text style={ss.cmpNavBtnText}>{c.emoji} {c.shortTitle}</Text>
                </Pressable>
              ))}
            </View>
          </ScrollView>
        </View>
      </Modal>
    </>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const ss = StyleSheet.create({
  scroll:  { flex: 1, backgroundColor: BG },
  content: {
    paddingTop:        Platform.OS === 'web' ? 28 : 18,
    paddingHorizontal: Platform.OS === 'web' ? 22 : 16,
    paddingBottom:     Platform.OS === 'web' ? 48 : 32,
    alignItems:        'center',
  },

  // Hero
  hero: {
    width:    '100%',
    maxWidth: Platform.OS === 'web' ? 940 : 720,
    marginBottom: 18,
  },
  heroIconRow: { alignItems: 'flex-end' },
  heroIcon: {
    width:           40,
    height:          40,
    borderRadius:    14,
    backgroundColor: PRIMARY_SOFT,
    borderWidth:     1,
    borderColor:     'rgba(200,83,30,0.18)',
    alignItems:      'center',
    justifyContent:  'center',
    marginBottom:    10,
  },
  heroTitle: {
    fontFamily: theme.fonts.bold,
    fontSize:   Platform.OS === 'web' ? 38 : 28,
    color:      TEXT,
    textAlign:  'right',
  },
  heroSub: {
    marginTop:  8,
    fontFamily: theme.fonts.regular,
    fontSize:   14.5,
    lineHeight: 24,
    color:      MUTED,
    textAlign:  'right',
  },

  // Stats bar
  statsBar: {
    marginTop:       14,
    flexDirection:   'row-reverse',
    backgroundColor: CARD,
    borderRadius:    16,
    borderWidth:     1,
    borderColor:     BORDER,
    padding:         14,
    ...(Platform.OS === 'web' ? { boxShadow: '0 4px 20px rgba(15,23,42,0.08)' } as any : {}),
  } as any,
  statItem:   { flex: 1, alignItems: 'center' },
  statNum:    { fontFamily: theme.fonts.bold, fontSize: 18, color: PRIMARY },
  statLabel:  { fontFamily: theme.fonts.regular, fontSize: 11, color: MUTED, marginTop: 2, textAlign: 'center' },
  statDivider:{ width: 1, backgroundColor: BORDER, marginVertical: 4 },

  // Search
  searchWrap: {
    width:           '100%',
    maxWidth:        Platform.OS === 'web' ? 940 : 720,
    flexDirection:   'row-reverse',
    alignItems:      'center',
    gap:             10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius:    16,
    backgroundColor: CARD,
    borderWidth:     1,
    borderColor:     BORDER,
    marginBottom:    12,
    ...(Platform.OS === 'web' ? { boxShadow: '0 4px 20px rgba(15,23,42,0.08)' } as any : {}),
  } as any,
  searchIcon: {
    width:           34,
    height:          34,
    borderRadius:    10,
    backgroundColor: PRIMARY_SOFT,
    borderWidth:     1,
    borderColor:     'rgba(200,83,30,0.18)',
    alignItems:      'center',
    justifyContent:  'center',
  },
  searchInput: {
    flex:       1,
    fontFamily: theme.fonts.regular,
    fontSize:   15,
    color:      TEXT,
    textAlign:  'right',
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } as any : {}),
  } as any,
  searchClear: { padding: 4 },

  // Category Tabs
  tabsScroll: {
    width:        '100%',
    maxWidth:     Platform.OS === 'web' ? 940 : 720,
    marginBottom: 10,
  },
  tabsRow: {
    flexDirection: 'row-reverse',
    gap:           8,
    paddingRight:  4,
  } as any,
  tab: {
    flexDirection:   'row-reverse',
    alignItems:      'center',
    gap:             4,
    paddingVertical: 7,
    paddingHorizontal: 14,
    borderRadius:    999,
    borderWidth:     1,
    borderColor:     BORDER,
    backgroundColor: CARD,
  } as any,
  tabEmoji:      { fontSize: 13 },
  tabText:       { fontFamily: theme.fonts.medium, fontSize: 13, color: MUTED },
  tabTextActive: { color: '#FAF5EA' },

  // Demand filter row
  filterRow: {
    width:         '100%',
    maxWidth:      Platform.OS === 'web' ? 940 : 720,
    flexDirection: 'row-reverse',
    alignItems:    'center',
    gap:           8,
    marginBottom:  10,
    flexWrap:      'wrap',
  } as any,
  demandWrap:    { position: 'relative' },
  demandBtn: {
    flexDirection: 'row-reverse',
    alignItems:    'center',
    gap:           6,
    paddingVertical: 7,
    paddingHorizontal: 12,
    borderRadius:  999,
    borderWidth:   1,
    borderColor:   BORDER,
    backgroundColor: CARD,
  } as any,
  demandBtnActive: { borderColor: 'rgba(200,83,30,0.18)', backgroundColor: PRIMARY_SOFT },
  demandBtnText:   { fontFamily: theme.fonts.medium, fontSize: 13, color: MUTED },
  demandDropdown: {
    position:        'absolute',
    top:             38,
    right:           0,
    backgroundColor: CARD,
    borderRadius:    12,
    borderWidth:     1,
    borderColor:     BORDER,
    zIndex:          999,
    minWidth:        140,
    ...(Platform.OS === 'web' ? { boxShadow: '0 8px 24px rgba(15,23,42,0.14)' } as any : {}),
  } as any,
  demandOpt:       { paddingVertical: 10, paddingHorizontal: 14 },
  demandOptActive: { backgroundColor: PRIMARY_SOFT },
  demandOptText:   { fontFamily: theme.fonts.medium, fontSize: 13, color: TEXT, textAlign: 'right' },

  clearBtn: {
    flexDirection: 'row-reverse',
    alignItems:    'center',
    gap:           4,
    paddingVertical: 7,
    paddingHorizontal: 10,
    borderRadius:  999,
    borderWidth:   1,
    borderColor:   BORDER,
    backgroundColor: CARD,
  } as any,
  clearBtnText: { fontFamily: theme.fonts.medium, fontSize: 12, color: MUTED },

  savedToggle: {
    flexDirection: 'row-reverse',
    alignItems:    'center',
    gap:           5,
    paddingVertical: 7,
    paddingHorizontal: 12,
    borderRadius:  999,
    borderWidth:   1,
    borderColor:   BORDER,
    backgroundColor: CARD,
  } as any,
  savedToggleActive: { borderColor: 'rgba(200,83,30,0.18)', backgroundColor: PRIMARY_SOFT },
  savedToggleText:   { fontFamily: theme.fonts.medium, fontSize: 13, color: MUTED },

  // Results meta
  resultsMeta: {
    width:         '100%',
    maxWidth:      Platform.OS === 'web' ? 940 : 720,
    flexDirection: 'row-reverse',
    alignItems:    'center',
    justifyContent:'space-between',
    marginBottom:  12,
  },
  resultsText: { fontFamily: theme.fonts.medium, fontSize: 13, color: MUTED },
  compareBar: {
    flexDirection: 'row-reverse',
    alignItems:    'center',
    gap:           5,
    paddingVertical: 5,
    paddingHorizontal: 12,
    borderRadius:  999,
    backgroundColor: PRIMARY_SOFT,
    borderWidth:   1,
    borderColor:   'rgba(200,83,30,0.18)',
  } as any,
  compareBarText: { fontFamily: theme.fonts.bold, fontSize: 12, color: PRIMARY },

  // Grid
  grid: {
    width:         '100%',
    maxWidth:      Platform.OS === 'web' ? 940 : 720,
    flexDirection: 'row-reverse',
    flexWrap:      'wrap',
    gap:           14,
    justifyContent:'flex-start',
  } as any,

  // Card
  card: {
    width:           Platform.OS === 'web' ? 292 : '100%',
    borderRadius:    18,
    backgroundColor: CARD,
    borderWidth:     1,
    borderColor:     BORDER,
    overflow:        'hidden',
    ...(Platform.OS === 'web' ? { boxShadow: '0 4px 24px rgba(15,23,42,0.08)' } as any : {}),
  } as any,
  cardPressed: { transform: [{ scale: 0.99 }], opacity: 0.96 },

  cardStrip: {
    paddingHorizontal: 14,
    paddingVertical:   10,
  },
  cardStripRow:  { flexDirection: 'row-reverse', alignItems: 'center', justifyContent: 'space-between' },
  cardEmoji:     { fontSize: 28 },
  cardStripRight:{ alignItems: 'flex-start', gap: 6 } as any,
  catRow:        { flexDirection: 'row-reverse', alignItems: 'center', gap: 5 } as any,
  catDot:        { width: 7, height: 7, borderRadius: 9999 },
  catLabel:      { fontFamily: theme.fonts.semiBold, fontSize: 11 },

  badge: {
    paddingVertical:   3,
    paddingHorizontal: 8,
    borderRadius:      999,
    borderWidth:       1,
  },
  badgeText: { fontFamily: theme.fonts.bold, fontSize: 10.5 },

  cardBody:  { padding: 14 },
  cardTitle: { fontFamily: theme.fonts.bold, fontSize: 16, color: TEXT, textAlign: 'right' },
  cardDesc:  {
    marginTop:  5,
    fontFamily: theme.fonts.regular,
    fontSize:   13,
    color:      MUTED,
    textAlign:  'right',
    lineHeight: 20,
  },

  // Metrics
  metaRow: {
    marginTop:      10,
    flexDirection:  'row-reverse',
    alignItems:     'center',
    backgroundColor:'rgba(15,23,42,0.03)',
    borderRadius:   10,
    paddingVertical: 8,
  },
  metaChip:    { flex: 1, alignItems: 'center' },
  metaLabel:   { fontFamily: theme.fonts.regular, fontSize: 10, color: MUTED },
  metaValue:   { fontFamily: theme.fonts.bold, fontSize: 12, color: TEXT, marginTop: 1 },
  metaDivider: { width: 1, height: 24, backgroundColor: BORDER },

  // Skills chips
  skillsRow: {
    marginTop:     8,
    flexDirection: 'row-reverse',
    flexWrap:      'wrap',
    gap:           5,
  } as any,
  skillChip: {
    paddingVertical:   3,
    paddingHorizontal: 8,
    borderRadius:      6,
    backgroundColor:   'rgba(200,83,30,0.18)',
    borderWidth:       1,
    borderColor:       'rgba(200,83,30,0.18)',
  },
  skillChipText: { fontFamily: theme.fonts.medium, fontSize: 11, color: PRIMARY },

  // Card actions
  cardActions: {
    marginTop:     12,
    flexDirection: 'row-reverse',
    alignItems:    'center',
    gap:           8,
  } as any,
  primaryBtn: {
    flex:            1,
    flexDirection:   'row-reverse',
    alignItems:      'center',
    justifyContent:  'center',
    gap:             6,
    backgroundColor: PRIMARY,
    borderRadius:    10,
    paddingVertical: 9,
    ...(Platform.OS === 'web' ? { boxShadow: '0 4px 14px rgba(200,83,30,0.18)' } as any : {}),
  } as any,
  primaryBtnText: { fontFamily: theme.fonts.bold, fontSize: 13, color: '#FAF5EA' },

  iconBtns: { flexDirection: 'row-reverse', gap: 6 } as any,
  iconBtn: {
    width:           34,
    height:          34,
    borderRadius:    10,
    backgroundColor: 'rgba(15,23,42,0.04)',
    borderWidth:     1,
    borderColor:     BORDER,
    alignItems:      'center',
    justifyContent:  'center',
  },
  iconBtnActive:   { backgroundColor: PRIMARY_SOFT, borderColor: 'rgba(200,83,30,0.18)' },
  iconBtnDisabled: { opacity: 0.4 },
  compareActive:   { backgroundColor: PRIMARY_SOFT, borderColor: 'rgba(200,83,30,0.18)' },

  // Empty state
  empty: {
    width:        '100%',
    backgroundColor: CARD,
    borderRadius: 0,
    borderWidth:  1,
    borderColor:  BORDER,
    padding:      24,
    alignItems:   'center',
    gap:          8,
  } as any,
  emptyEmoji: { fontSize: 36 },
  emptyTitle: { fontFamily: theme.fonts.bold, fontSize: 16, color: TEXT },
  emptyText:  { fontFamily: theme.fonts.regular, fontSize: 13.5, color: MUTED, textAlign: 'center', lineHeight: 22 },
  emptyBtn: {
    marginTop:       8,
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius:    12,
    backgroundColor: PRIMARY,
  },
  emptyBtnText: { fontFamily: theme.fonts.bold, fontSize: 13, color: '#FAF5EA' },

  // CTA Section
  ctaSection: {
    width:           '100%',
    maxWidth:        Platform.OS === 'web' ? 940 : 720,
    marginTop:       28,
    backgroundColor: CARD,
    borderRadius:    20,
    borderWidth:     1,
    borderColor:     BORDER,
    padding:         20,
    alignItems:      'flex-end',
    ...(Platform.OS === 'web' ? { boxShadow: '0 4px 24px rgba(15,23,42,0.08)' } as any : {}),
  } as any,
  ctaTitle: { fontFamily: theme.fonts.bold, fontSize: 18, color: TEXT },
  ctaSub:   { fontFamily: theme.fonts.regular, fontSize: 13.5, color: MUTED, marginTop: 4, textAlign: 'right' },
  ctaBtns:  { flexDirection: 'row-reverse', gap: 10, marginTop: 14 } as any,
  ctaBtn: {
    flexDirection:   'row-reverse',
    alignItems:      'center',
    gap:             7,
    backgroundColor: PRIMARY,
    paddingVertical: 11,
    paddingHorizontal: 18,
    borderRadius:    12,
    ...(Platform.OS === 'web' ? { boxShadow: '0 4px 14px rgba(200,83,30,0.18)' } as any : {}),
  } as any,
  ctaBtnText: { fontFamily: theme.fonts.bold, fontSize: 13.5, color: '#FAF5EA' },
  ctaBtnSecondary: {
    paddingVertical:   11,
    paddingHorizontal: 18,
    borderRadius:      12,
    borderWidth:       1,
    borderColor:       'rgba(200,83,30,0.18)',
    backgroundColor:   PRIMARY_SOFT,
  },
  ctaBtnSecondaryText: { fontFamily: theme.fonts.bold, fontSize: 13.5, color: PRIMARY },

  // Sticky compare panel
  comparePanel: {
    position:  'absolute',
    bottom:    0,
    left:      0,
    right:     0,
    backgroundColor: CARD,
    borderTopWidth:  1,
    borderTopColor:  BORDER,
    paddingVertical: 12,
    paddingHorizontal: 16,
    ...(Platform.OS === 'web' ? { boxShadow: '0 -4px 20px rgba(15,23,42,0.10)' } as any : {}),
  } as any,
  comparePanelInner: {
    flexDirection: 'row-reverse',
    alignItems:    'center',
    justifyContent:'space-between',
  },
  comparePanelText:  { fontFamily: theme.fonts.medium, fontSize: 14, color: TEXT },
  comparePanelBtns:  { flexDirection: 'row-reverse', alignItems: 'center', gap: 8 } as any,
  comparePanelClear: {
    width:           32,
    height:          32,
    borderRadius:    8,
    borderWidth:     1,
    borderColor:     BORDER,
    alignItems:      'center',
    justifyContent:  'center',
  },
  comparePanelBtn: {
    flexDirection:   'row-reverse',
    alignItems:      'center',
    gap:             6,
    backgroundColor: PRIMARY,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius:    10,
  } as any,
  comparePanelBtnText: { fontFamily: theme.fonts.bold, fontSize: 13, color: '#FAF5EA' },

  // Compare Modal
  modalContainer: { flex: 1, backgroundColor: BG },
  modalHeader: {
    flexDirection:     'row-reverse',
    alignItems:        'center',
    justifyContent:    'space-between',
    padding:           16,
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
    backgroundColor:   CARD,
  },
  modalClose: {
    width:           36,
    height:          36,
    borderRadius:    10,
    borderWidth:     1,
    borderColor:     BORDER,
    alignItems:      'center',
    justifyContent:  'center',
  },
  modalTitle:   { fontFamily: theme.fonts.bold, fontSize: 18, color: TEXT },
  modalScroll:  { flex: 1 },
  modalContent: { padding: 16, paddingBottom: 32 },

  // Compare table
  cmpHeaderRow: {
    flexDirection: 'row-reverse',
    alignItems:    'center',
    gap:           6,
    marginBottom:  8,
  } as any,
  cmpColEmpty:  { flex: 1 },
  cmpColHeader: { flex: 1.2, alignItems: 'center', gap: 4 } as any,
  cmpColEmoji:  { fontSize: 24 },
  cmpColTitle:  { fontFamily: theme.fonts.bold, fontSize: 12, color: TEXT, textAlign: 'center' },
  cmpDivider:   { height: 1, backgroundColor: BORDER, marginVertical: 8 },

  cmpRow: {
    flexDirection:     'row-reverse',
    alignItems:        'center',
    paddingVertical:   10,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(15,23,42,0.05)',
    gap:               6,
  } as any,
  cmpLabel: {
    flex:       1,
    fontFamily: theme.fonts.medium,
    fontSize:   12.5,
    color:      MUTED,
    textAlign:  'right',
  },
  cmpValue: {
    flex:       1.2,
    fontFamily: theme.fonts.bold,
    fontSize:   12.5,
    color:      TEXT,
    textAlign:  'center',
  },

  cmpSectionTitle: {
    fontFamily:  theme.fonts.bold,
    fontSize:    14,
    color:       TEXT,
    textAlign:   'right',
    marginTop:   16,
    marginBottom:8,
  },
  cmpProsRow: { flexDirection: 'row-reverse', gap: 6 } as any,
  cmpProsCol: { flex: 1, gap: 6 } as any,
  cmpProItem: { flexDirection: 'row-reverse', alignItems: 'flex-start', gap: 5 } as any,
  cmpProDot:  { fontFamily: theme.fonts.bold, fontSize: 12, color: '#10B981' },
  cmpProText: { flex: 1, fontFamily: theme.fonts.regular, fontSize: 11.5, color: TEXT, textAlign: 'right' },

  cmpNavRow: {
    flexDirection: 'row-reverse',
    gap:           8,
    marginTop:     20,
  } as any,
  cmpNavBtn: {
    flex:            1,
    alignItems:      'center',
    justifyContent:  'center',
    paddingVertical: 10,
    borderRadius:    12,
    borderWidth:     1,
    borderColor:     'rgba(200,83,30,0.18)',
    backgroundColor: PRIMARY_SOFT,
  },
  cmpNavBtnText: { fontFamily: theme.fonts.bold, fontSize: 13, color: PRIMARY },
});
